import * as z from "zod";
import type { ApiContext } from "../context";
import { type ApiNotAuthorized, InvalidFormField, UnexpectedApiError, ValidationError } from "../errors";
import { type AppPlatform, AppVersionCheck, AppVersionPolicy, type AppVersionPolicyInput } from "../model/app-version";
import { parse } from "../model/common";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";

const PoliciesEnvelope = z.object({ policies: z.array(AppVersionPolicy) });

/** `/app`: the mobile apps' update gate. Unauthenticated; pass `anonymous`. */
export class AppVersionApi {
  constructor(private readonly t: Transport) {}

  /** `version` is the marketing version (`2.21`), never the build number. */
  async check(
    ctx: ApiContext,
    platform: AppPlatform,
    version: string,
  ): Promise<Result<AppVersionCheck, InvalidFormField>> {
    const r = await this.t.get(ctx, "/app/version", { query: { platform, version } });
    return forward(
      r,
      (j) => parse(AppVersionCheck, j),
      (e) => {
        if (e instanceof ValidationError) return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        throw new UnexpectedApiError(e);
      },
    );
  }
}

/** Platform staff: `/admin/app-versions`, the policy `AppVersionApi.check` answers from. */
export class AdminAppVersionsApi {
  constructor(private readonly t: Transport) {}

  /** Both platforms, whether or not either has ever been configured. */
  async list(ctx: ApiContext): Promise<Result<AppVersionPolicy[], ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/admin/app-versions");
    return forward(
      r,
      (j) => parse(PoliciesEnvelope, j).policies,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** Replaces the platform's whole policy. A minimum above the latest is refused. */
  async set(
    ctx: ApiContext,
    platform: AppPlatform,
    policy: AppVersionPolicyInput,
  ): Promise<Result<AppVersionPolicy, ApiNotAuthorized | InvalidFormField>> {
    const r = await this.t.put(ctx, `/admin/app-versions/${platform}`, { body: policy });
    return forward(
      r,
      (j) => parse(AppVersionPolicy, j),
      (e) => {
        if (e instanceof ValidationError) return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        return e;
      },
    );
  }
}
